/**
 * Curriculum API Service
 * Fetches boards, grades, subjects, and chapters from the backend
 */
import axios from 'axios';
import { apiUrl, applyInterceptors } from './config';
import { getAuthToken, getPortalType } from '../utils/auth';

// Create axios instance with shared interceptors (including auth, fingerprint, and rate limits)
const curriculumApi = axios.create();
applyInterceptors(curriculumApi);

// Subjects API
export const subjectsAPI = {
  list: async (params = {}, signal) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/subjects/'), {
        params,
        signal,
      });
      const data = response.data?.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching subjects:', error);
      throw error;
    }
  },
  get: async (id) => {
    try {
      const response = await curriculumApi.get(apiUrl(`superadmin/subjects/${id}/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching subject ${id}:`, error);
      throw error;
    }
  },
};

// Chapters API
export const chaptersAPI = {
  list: async (params = {}, signal) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/chapters/'), {
        params,
        signal,
      });
      const data = response.data?.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching chapters:', error);
      throw error;
    }
  },
  get: async (id) => {
    try {
      const response = await curriculumApi.get(apiUrl(`superadmin/chapters/${id}/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching chapter ${id}:`, error);
      throw error;
    }
  },

  getBudleeV2Content: async (chapterId, options = {}) => {
    try {
      const forceFresh = !!options.forceFresh;
      const response = await curriculumApi.get(apiUrl(`superadmin/chapters/${chapterId}/budlee-content-v2/`), {
        params: forceFresh ? { _: Date.now() } : undefined,
        headers: forceFresh ? {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        } : undefined,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching Budlee v2 content for chapter ${chapterId}:`, error);
      throw error;
    }
  },

  getProcessingStatus: async (chapterId) => {
    try {
      const response = await curriculumApi.get(apiUrl(`superadmin/chapters/${chapterId}/processing-status/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching processing status for chapter ${chapterId}:`, error);
      throw error;
    }
  },

  getSections: async (chapterId) => {
    try {
      // Use userportal prefix to match backend routing structure
      const response = await curriculumApi.get(apiUrl(`userportal/superadmin/chapters/${chapterId}/sections/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching sections for chapter ${chapterId}:`, error);
      throw error;
    }
  },

  getChapterOverview: async (chapterId) => {
    try {
      const response = await curriculumApi.get(apiUrl(`userportal/chapters/${chapterId}/overview/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching chapter overview for chapter ${chapterId}:`, error);
      throw error;
    }
  },

  getTaskStatus: async (taskId) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/chapters/task-status/'), {
        params: { task_id: taskId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching task status for task ${taskId}:`, error);
      throw error;
    }
  },

  getAnnotations: async (params = {}) => {
    try {
      const response = await curriculumApi.get(apiUrl('superadmin/chapter-annotations/'), {
        params
      });
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching chapter annotations:', error);
      throw error;
    }
  },

  getMathProblems: async (chapterId) => {
    try {
      const response = await curriculumApi.get(apiUrl(`core/chapters/${chapterId}/math-problems/`));
      return response.data;
    } catch (error) {
      console.error(`Error fetching math problems for chapter ${chapterId}:`, error);
      throw error;
    }
  },
};

// Standalone export for generateFormulaLatex (used in StudentNoteCorrectionDialog)
export const generateFormulaLatex = async (chapterId, text, customPrompt = '') => {
  try {
    const response = await curriculumApi.post(apiUrl(`userportal/superadmin/chapters/${chapterId}/short-notes-formulas-generate/`), {
      topic: text,
      custom_prompt: customPrompt
    }, { timeout: 120000 });
    return response.data;
  } catch (error) {
    console.error('Error generating formula:', error);
    throw error;
  }
};

export const getShortNotesFormulaBank = async (chapterId, refresh = false) => {
  try {
    const response = await curriculumApi.get(apiUrl(`userportal/superadmin/chapters/${chapterId}/short-notes-formulas/`), {
      params: refresh ? { refresh: true } : undefined,
      timeout: 120000,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching formula bank:', error);
    throw error;
  }
};


// Curriculum API (unified object for Chapters and Sections)
export const curriculumAPI = {
  getChapter: async (id) => chaptersAPI.get(id),

  getChapterPDFSections: async (chapterId, pdfId = null) => {
    try {
      const params = pdfId ? { pdf_id: pdfId } : {};
      // Use userportal prefix to match backend routing structure
      const response = await curriculumApi.get(apiUrl(`userportal/superadmin/chapters/${chapterId}/sections/`), { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching sections for chapter ${chapterId}:`, error);
      throw error;
    }
  },

  getPDFProcessingStatus: async (chapterId) => chaptersAPI.getProcessingStatus(chapterId),
};
